import type {
    Cart,
    EccoAddDiscountToCartMutationVariables,
    EccoGetMyActiveCartQueryVariables,
    EccoGetMyActiveDataQueryVariables,
    EccoRemoveDiscountFromCartMutationVariables,
    EccoRestoreProductToCartMutationVariables,
    GetMasterProductsQueryVariables,
    ProductQueryResult,
    RecalculateCartMutationVariables,
} from "@graphql/generated/types";
import {
    EccoGetMyActiveCartDocument,
    EccoGetMyActiveDataDocument,
    GetMasterProductsDocument,
} from "@graphql/generated/sdk";
import { useCallback, useMemo } from "react";
import { useLocale } from "next-intl";

import type { CreateCartParams } from "@app/api/(commerce)/cart/create-cart/route";
import { useCommerceToolsClient } from "@lib/commerce/adapters/commercetools/useCommerceToolsClient";
import { ClientFetchService } from "@lib/helpers/clientFetchService";
import { updateMutation, updateQuery } from "@store/helpers";
import { useStore } from "@store/store";

export const useCreateMyCart = () => {
    const setCartState = useStore((state) => state.setCart);
    return useCallback(
        async (body: CreateCartParams) => {
            const cart = await ClientFetchService.post<CreateCartParams, Cart>(
                `/api/cart/create-cart`,
                body
            );
            setCartState(cart);
            return cart;
        },
        [setCartState]
    );
};

export const useGetMyActiveCart = () => {
    const { baseClient } = useCommerceToolsClient();
    const locale = useLocale();
    const setData = useStore((state) => state.setCart);

    return useMemo(
        () =>
            updateQuery<EccoGetMyActiveCartQueryVariables>(
                baseClient,
                setData,
                (data) => data.me.activeCart,
                EccoGetMyActiveCartDocument,
                { locale }
            ),
        [baseClient, locale, setData]
    );
};

export const useGetMyActiveData = () => {
    const { baseClient } = useCommerceToolsClient();
    const locale = useLocale();
    const setData = useStore((state) => state.setActiveData);

    const selector = (data: any) => {
        const payload = {
            cart: undefined,
            wishlist: undefined,
        };
        if (data.me?.activeCart) {
            payload.cart = data.me.activeCart;
        }
        if (data.me?.shoppingLists?.results?.length > 0) {
            payload.wishlist = data.me.shoppingLists.results[0];
        }

        return payload;
    };

    return updateQuery<EccoGetMyActiveDataQueryVariables>(
        baseClient,
        setData,
        selector,
        EccoGetMyActiveDataDocument,
        {
            locale,
        }
    );
};

export const useGetMasterDataForLineItems = () => {
    const { baseClient } = useCommerceToolsClient();
    const setData = useStore((state) => state.setMasterProducts);

    const selector = (data: { products: ProductQueryResult }) => {
        const map = new Map();

        data.products?.results?.forEach((item) => {
            if (!map.has(item.id)) map.set(item.id, item.masterData.current);
        });

        return map;
    };

    const filter = {
        skus: "$skus",
        channelIds: "$channelIds",
    };

    return updateQuery<GetMasterProductsQueryVariables>(
        baseClient,
        setData,
        selector,
        GetMasterProductsDocument,
        filter
    );
};

export const useApplyDiscountCodeToCart = () => {
    const setData = useStore((state) => state.setCart);
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.eccoAddDiscountToCart;
    return updateMutation<EccoAddDiscountToCartMutationVariables>(executeWithVariables, setData);
};

export const useRemoveDiscountCodeFromCart = () => {
    const setData = useStore((state) => state.setCart);
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.eccoRemoveDiscountFromCart;
    return updateMutation<EccoRemoveDiscountFromCartMutationVariables>(
        executeWithVariables,
        setData
    );
};

export const useRecalculateCart = () => {
    const setData = useStore((state) => state.setCart);
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.recalculateCart;
    return updateMutation<RecalculateCartMutationVariables>(executeWithVariables, setData);
};

export const useRestoreProductToCart = () => {
    const { client } = useCommerceToolsClient();
    const executeWithVariables = client.eccoRestoreProductToCart;
    const setData = useStore((state) => state.setCart);
    return updateMutation<EccoRestoreProductToCartMutationVariables>(executeWithVariables, setData);
};

const isCart = (value: unknown): value is Cart => {
    if (!value || typeof value !== "object") {
        return false;
    }

    const cart = value as {
        id?: unknown;
        lineItems?: unknown;
        totalPrice?: unknown;
    };

    // Check top-level required fields
    if (
        typeof cart.id !== "string" ||
        !Array.isArray(cart.lineItems) ||
        typeof cart.totalPrice !== "object" ||
        cart.totalPrice === null
    ) {
        return false;
    }

    // Validate each lineItem has required fields
    for (const item of cart.lineItems) {
        if (
            !item ||
            typeof item !== "object" ||
            typeof (item as any).id !== "string" ||
            typeof (item as any).quantity !== "number"
        ) {
            return false;
        }
    }

    // Validate totalPrice has required MoneyFragment fields
    const totalPrice = cart.totalPrice as any;
    if (typeof totalPrice.centAmount !== "number" || typeof totalPrice.currencyCode !== "string") {
        return false;
    }

    return true;
};

export const useGetActiveCart = () => {
    const locale = useLocale();
    const setCart = useStore((state) => state.setCart);
    const { client } = useCommerceToolsClient();

    return async (): Promise<Cart | undefined> => {
        const response = await client.eccoGetMyActiveCart({ locale });

        // Check for GraphQL errors before processing data
        if (response?.errors && response.errors.length > 0) {
            const errorMessages = response.errors.map((e) => e.message).join(", ");
            throw new Error(`Failed to get active cart: ${errorMessages}`);
        }

        const activeCart = response?.data?.me?.activeCart;
        const cart = isCart(activeCart) ? activeCart : undefined;
        setCart(cart);
        return cart;
    };
};

export const useGetMyCartById = () => {
    const locale = useLocale();
    const { client } = useCommerceToolsClient();

    return async (id: string): Promise<Cart | undefined> => {
        const response = await client.eccoGetMyCart({ id, locale });
        if (response?.errors && response.errors.length > 0) {
            const errorMessages = response.errors.map((e) => e.message).join(", ");
            throw new Error(`Failed to get cart: ${errorMessages}`);
        }
        const cart = response?.data?.me?.cart;
        return isCart(cart) ? cart : undefined;
    };
};
